import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { 
  Plus, Upload, DollarSign, FileText, CheckCircle, 
  Star, X, AlertCircle, ArrowUpDown, ChevronDown, ChevronUp, Clock
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom'; // Added for safety redirect

const SetterDashboard = () => {
  // 1. ADD LOADING STATE
  const [loading, setLoading] = useState(true); 
  const navigate = useNavigate();

  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ 
    total: 0, 
    approved: 0, 
    earnings: 0, 
    avgRating: 0,
    pendingReview: 0,
    pendingPayout: 0
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [notification, setNotification] = useState(null);
  const [expandedLeadId, setExpandedLeadId] = useState(null);
  
  const [sortConfig, setSortConfig] = useState({ key: 'status', direction: 'desc' });

  const [formData, setFormData] = useState({
    business_name: '',
    category: 'commercial-cleaning',
    street: '', city: '', state: '', zip: '', country: '',
    appointment_date: '', appointment_time: '',
    decision_maker_name: '', 
    facility_size: '', facility_type: '', company_size: '',
    scope_of_work: '', frequency: 'one-time',
    email: '', phone: '', urgency: 'medium',
    price_company: '', proof_file: null
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 3000);
  };

  const fetchDashboardData = async () => {
    try {
      const response = await api.get('setter/dashboard/');
      
      // 2. SAFEGUARD: Ensure data is an array
      const data = Array.isArray(response.data) ? response.data : [];
      
      const sortedInitial = data.sort((a, b) => {
        if (a.status === 'PENDING' && b.status !== 'PENDING') return -1;
        if (a.status !== 'PENDING' && b.status === 'PENDING') return 1;
        return new Date(b.created_at) - new Date(a.created_at);
      });

      setLeads(sortedInitial);
      
      const approvedCount = data.filter(l => l.status === 'APPROVED' || l.status === 'PURCHASED').length;
      const pendingReviewCount = data.filter(l => l.status === 'PENDING').length;
      
      const earningsTotal = data.reduce((sum, lead) => {
        return lead.status === 'PURCHASED' ? sum + Number(lead.payout_setter || 0) : sum;
      }, 0);

      const pendingPayoutTotal = data.reduce((sum, lead) => {
        return lead.status === 'APPROVED' ? sum + Number(lead.payout_setter || 0) : sum;
      }, 0);

      const ratedLeads = data.filter(l => Number(l.rating) > 0);
      const avgRatingVal = ratedLeads.length 
        ? (ratedLeads.reduce((sum, l) => sum + Number(l.rating), 0) / ratedLeads.length).toFixed(1)
        : 0;
      
      setStats({ 
        total: data.length, 
        approved: approvedCount, 
        earnings: earningsTotal, 
        avgRating: avgRatingVal,
        pendingReview: pendingReviewCount,
        pendingPayout: pendingPayoutTotal
      });

    } catch (error) {
      console.error("Error loading dashboard", error);
      // Safety: If unauthorized, redirect to login
      if (error.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      // 3. STOP LOADING SPINNER
      setLoading(false);
    }
  };

  const toggleExpand = (id) => {
    setExpandedLeadId(expandedLeadId === id ? null : id);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedLeads = () => {
    // Safety check for leads array
    const sortable = [...(leads || [])];
    if (sortConfig.key !== null) {
      sortable.sort((a, b) => {
        if (sortConfig.key === 'status') {
            const rank = { 'PENDING': 3, 'APPROVED': 2, 'PURCHASED': 1, 'REJECTED': 0 };
            return sortConfig.direction === 'asc' 
                ? rank[a.status] - rank[b.status]
                : rank[b.status] - rank[a.status];
        }
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortable;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleFileChange = (e) => {
    setFormData({ ...formData, proof_file: e.target.files[0] });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = new FormData();

    const fullAddress = `${formData.street}, ${formData.city}, ${formData.state} ${formData.zip}, ${formData.country}`;
    data.append('address', fullAddress);

    data.append('business_name', formData.business_name);
    data.append('category', formData.category);
    data.append('street', formData.street);
    data.append('city', formData.city);
    data.append('state', formData.state);
    data.append('zip_code', formData.zip);
    data.append('country', formData.country);
    
    data.append('appointment_date', formData.appointment_date);
    data.append('appointment_time', formData.appointment_time);
    data.append('urgency', formData.urgency);
    data.append('decision_maker_name', formData.decision_maker_name);
    data.append('decision_maker_role', formData.decision_maker_role || 'Manager');
    data.append('phone', formData.phone);
    data.append('email', formData.email);
    data.append('scope_of_work', formData.scope_of_work);
    
    if (formData.facility_size) data.append('facility_size', formData.facility_size);
    if (formData.facility_type) data.append('facility_type', formData.facility_type);
    if (formData.company_size) data.append('company_size', formData.company_size);
    if (formData.frequency) data.append('frequency', formData.frequency);

    if (formData.price_company === '') {
       data.append('price_company', '0');
    } else {
       data.append('price_company', formData.price_company);
    }
    
    if (formData.proof_file) {
      data.append('proof_file', formData.proof_file);
    }

    try {
      await api.post('setter/submit/', data, {
        headers: { 'Content-Type': undefined }
      });
      
      setIsModalOpen(false);
      fetchDashboardData(); 
      showNotification('success', 'Lead submitted successfully! Pending approval.');
      
      setFormData({
        business_name: '', category: 'commercial-cleaning',
        street: '', city: '', state: '', zip: '', country: '',
        appointment_date: '', appointment_time: '',
        decision_maker_name: '', facility_size: '', facility_type: '', company_size: '',
        scope_of_work: '', frequency: 'one-time',
        email: '', phone: '', urgency: 'medium',
        price_company: '', proof_file: null
      });
    } catch (error) {
      console.error("Upload failed", error);
      const errorMsg = error.response?.data 
        ? Object.entries(error.response.data).map(([k, v]) => `${k}: ${v}`).join(', ')
        : error.message;
      showNotification('error', `Failed: ${errorMsg}`);
    }
  };

  const formatCategory = (slug) => {
    if(!slug) return '';
    return slug.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  // 4. CRITICAL FIX: Render Loading Screen if fetching
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 font-[Poppins]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-500 font-medium">Loading Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6 px-4 sm:px-6 lg:px-8 relative font-[Poppins]">
      
      <AnimatePresence>
        {notification && (
          <motion.div 
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            className={`fixed top-24 right-4 z-[60] flex items-center p-4 rounded-lg shadow-2xl border-l-4 ${
              notification.type === 'success' ? 'bg-white border-green-500 text-green-700' : 'bg-white border-red-500 text-red-700'
            }`}
          >
            <div className="mr-3">
              {notification.type === 'success' ? <CheckCircle size={24} /> : <AlertCircle size={24} />}
            </div>
            <div className="font-bold text-sm md:text-base">{notification.message}</div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="max-w-7xl mx-auto">
        
        {/* --- HEADER --- */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <motion.div initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }}>
            <h1 className="text-3xl md:text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-primary to-blue-800 tracking-tight">
              Setter Dashboard
            </h1>
            <p className="text-gray-500 mt-1 text-sm md:text-base">Track your performance and upload new opportunities.</p>
          </motion.div>
          <motion.button 
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsModalOpen(true)}
            className="flex items-center bg-gradient-to-r from-primary to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-6 py-3 rounded-full font-bold shadow-lg transition-all text-sm md:text-base w-full md:w-auto justify-center"
          >
            <Plus size={20} className="mr-2" /> Post New Lead
          </motion.button>
        </div>

        {/* --- STATS GRID --- */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          <StatCard icon={<FileText className="text-blue-500" />} label="Total Leads" value={stats.total} delay={0} />
          <StatCard icon={<Clock className="text-orange-500" />} label="Pending Review" value={stats.pendingReview} delay={0.05} />
          <StatCard icon={<CheckCircle className="text-green-500" />} label="Approved" value={stats.approved} delay={0.1} />
          <StatCard icon={<Star className="text-yellow-500" />} label="Avg Rating" value={stats.avgRating} delay={0.15} />
          <StatCard icon={<DollarSign className="text-yellow-600" />} label="Pending Payout" value={`$${stats.pendingPayout}`} delay={0.2} />
          <StatCard icon={<DollarSign className="text-purple-500" />} label="Total Earnings" value={`$${stats.earnings}`} delay={0.25} />
        </div>

        {/* --- LEAD RECORDS CONTAINER --- */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
          <div className="px-6 py-4 border-b border-gray-100 bg-gray-50 flex justify-between items-center">
            <h2 className="text-lg md:text-xl font-bold text-gray-700">Lead Records</h2>
            <span className="text-xs text-gray-400 italic">Click row to view details</span>
          </div>

          {/* --- DESKTOP TABLE VIEW --- */}
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <SortableHeader label="Date" sortKey="appointment_date" activeSort={sortConfig} onSort={handleSort} />
                  <SortableHeader label="Business" sortKey="business_name" activeSort={sortConfig} onSort={handleSort} />
                  <SortableHeader label="Category" sortKey="category" activeSort={sortConfig} onSort={handleSort} />
                  <SortableHeader label="Status" sortKey="status" activeSort={sortConfig} onSort={handleSort} />
                  <SortableHeader label="Rating" sortKey="rating" activeSort={sortConfig} onSort={handleSort} />
                  <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Payout</th>
                  <th className="px-6 py-3"></th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {getSortedLeads().map((lead, index) => (
                  <React.Fragment key={lead.id}>
                    <motion.tr 
                      initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: index * 0.05 }}
                      className={`hover:bg-blue-50 transition-colors cursor-pointer ${expandedLeadId === lead.id ? 'bg-blue-50' : ''}`}
                      onClick={() => toggleExpand(lead.id)}
                    >
                      <td className="px-6 py-4 text-sm text-gray-500">{lead.appointment_date}</td>
                      <td className="px-6 py-4 font-bold text-gray-900">{lead.business_name}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        <span className="bg-gray-100 px-2 py-1 rounded-md text-xs font-medium">{formatCategory(lead.category)}</span>
                      </td>
                      <td className="px-6 py-4"><StatusBadge status={lead.status} /></td>
                      <td className="px-6 py-4">
                        <div className="flex items-center text-yellow-500">
                          <span className="font-bold text-gray-700 mr-1">{Number(lead.rating) > 0 ? lead.rating : '-'}</span>
                          <Star size={14} fill={Number(lead.rating) > 0 ? "currentColor" : "none"} />
                        </div>
                      </td>
                      <td className="px-6 py-4 font-bold text-sm">
                        {lead.status === 'PURCHASED' ? <span className="text-green-600">${lead.payout_setter}</span> : 
                         lead.status === 'APPROVED' ? <span className="text-yellow-600 italic text-xs">Pending</span> : 
                         <span className="text-gray-400">-</span>}
                      </td>
                      <td className="px-6 py-4 text-right">
                        {expandedLeadId === lead.id ? <ChevronUp size={18} className="text-gray-400"/> : <ChevronDown size={18} className="text-gray-400"/>}
                      </td>
                    </motion.tr>
                    
                    <AnimatePresence>
                      {expandedLeadId === lead.id && (
                        <motion.tr 
                          initial={{ opacity: 0, height: 0 }} 
                          animate={{ opacity: 1, height: 'auto' }} 
                          exit={{ opacity: 0, height: 0 }}
                          className="bg-gray-50"
                        >
                          <td colSpan="7" className="px-6 py-4 border-b border-gray-200">
                            <LeadDetailsBlock lead={lead} />
                          </td>
                        </motion.tr>
                      )}
                    </AnimatePresence>
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {/* --- MOBILE CARD VIEW --- */}
          <div className="block md:hidden p-4 space-y-4">
            {getSortedLeads().map((lead) => (
              <div key={lead.id} className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm transition-all">
                <div className="flex justify-between items-start mb-2" onClick={() => toggleExpand(lead.id)}>
                  <div>
                    <h3 className="font-bold text-gray-900 text-lg">{lead.business_name}</h3>
                    <p className="text-xs text-gray-500">{formatCategory(lead.category)}</p>
                  </div>
                  <StatusBadge status={lead.status} />
                </div>
                
                <div className="grid grid-cols-2 gap-2 text-sm text-gray-600 mb-3" onClick={() => toggleExpand(lead.id)}>
                  <div className="flex items-center"><span className="text-gray-400 mr-1">Date:</span> {lead.appointment_date}</div>
                  <div className="flex items-center text-yellow-600">
                    <Star size={14} className="mr-1" fill={Number(lead.rating) > 0 ? "currentColor" : "none"} />
                    {Number(lead.rating) > 0 ? lead.rating : 'N/A'}
                  </div>
                </div>

                <AnimatePresence>
                  {expandedLeadId === lead.id && (
                    <motion.div 
                      initial={{ opacity: 0, height: 0 }} 
                      animate={{ opacity: 1, height: 'auto' }} 
                      exit={{ opacity: 0, height: 0 }}
                      className="border-t border-gray-100 pt-3 mb-3 overflow-hidden"
                    >
                      <LeadDetailsBlock lead={lead} isMobile />
                    </motion.div>
                  )}
                </AnimatePresence>

                <div className="flex justify-between items-center border-t border-gray-100 pt-3">
                  <div className="text-xs text-gray-400">Payout Status</div>
                  <div className="font-bold">
                    {lead.status === 'PURCHASED' ? <span className="text-green-600 text-lg">${lead.payout_setter}</span> : 
                     lead.status === 'APPROVED' ? <span className="text-yellow-600 italic text-xs">Pending</span> : 
                     <span className="text-gray-400 text-sm">-</span>}
                  </div>
                  <button onClick={() => toggleExpand(lead.id)} className="text-primary text-xs font-bold flex items-center">
                    {expandedLeadId === lead.id ? 'Close' : 'View Details'} 
                    {expandedLeadId === lead.id ? <ChevronUp size={14} className="ml-1"/> : <ChevronDown size={14} className="ml-1"/>}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {leads.length === 0 && <div className="p-10 text-center text-gray-500">No leads submitted yet. Start earning!</div>}
        </div>
      </div>

      {/* --- POST NEW LEAD MODAL --- */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm p-4">
            <motion.div 
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto font-[Poppins]"
            >
              <div className="sticky top-0 bg-white z-10 px-6 py-4 md:px-8 md:py-5 border-b border-gray-100 flex justify-between items-center">
                <div>
                  <h3 className="text-xl md:text-2xl font-bold text-gray-900">Post New Lead</h3>
                  <p className="text-xs md:text-sm text-gray-500">Fill all required fields to ensure quick approval</p>
                </div>
                <button onClick={() => setIsModalOpen(false)} className="p-2 bg-gray-100 rounded-full hover:bg-gray-200 transition">
                  <X size={20} className="text-gray-600" />
                </button>
              </div>
              
              <form onSubmit={handleSubmit} className="p-6 md:p-8 space-y-8">
                <Section title="Business Information" icon={<BuildingIcon />}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <Input label="Business Name *" name="business_name" placeholder="e.g. Empire Cleaners" value={formData.business_name} onChange={handleInputChange} required />
                      <Input label="Category *" name="category" type="select" value={formData.category} onChange={handleInputChange}>
                        <option value="commercial-cleaning">Commercial Cleaning</option>
                        <option value="janitorial">Janitorial Services</option>
                        <option value="mca">MCA</option>
                        <option value="real-estate">Real Estate</option>
                        <option value="financial">Financial Services</option>
                        <option value="healthcare">Healthcare</option>
                        <option value="technology">Technology</option>
                      </Input>
                      <div className="md:col-span-2 space-y-4">
                        <Input label="Street Address *" name="street" placeholder="123 Main St, Suite 100" value={formData.street} onChange={handleInputChange} required />
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            <Input label="City *" name="city" placeholder="New York" value={formData.city} onChange={handleInputChange} required />
                            <Input label="State *" name="state" placeholder="NY" value={formData.state} onChange={handleInputChange} required />
                            <Input label="Zip Code *" name="zip" placeholder="10001" value={formData.zip} onChange={handleInputChange} required />
                            <Input label="Country *" name="country" placeholder="USA" value={formData.country} onChange={handleInputChange} required />
                        </div>
                      </div>
                    </div>
                </Section>

                <Section title="Decision Maker" icon={<PersonIcon />}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <Input label="Full Name *" name="decision_maker_name" placeholder="e.g. John Smith" value={formData.decision_maker_name} onChange={handleInputChange} required />
                      <Input label="Phone Number *" name="phone" placeholder="(555) 123-4567" value={formData.phone} onChange={handleInputChange} required />
                      <Input label="Email Address *" name="email" type="email" placeholder="john@company.com" value={formData.email} onChange={handleInputChange} required />
                    </div>
                </Section>

                <Section title="Appointment Details" icon={<CalendarIcon />}>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <Input label="Date *" name="appointment_date" type="date" value={formData.appointment_date} onChange={handleInputChange} required />
                      <Input label="Time *" name="appointment_time" type="time" value={formData.appointment_time} onChange={handleInputChange} required />
                      <Input label="Urgency *" name="urgency" type="select" value={formData.urgency} onChange={handleInputChange}>
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                      </Input>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-4">
                      <Input label="Facility Type" name="facility_type" placeholder="e.g. Office, Warehouse" value={formData.facility_type} onChange={handleInputChange} />
                      <Input label="Facility Size" name="facility_size" placeholder="e.g. 10,000 sq ft" value={formData.facility_size} onChange={handleInputChange} />
                      <Input label="Company Size" name="company_size" placeholder="e.g. 10-50 employees" value={formData.company_size} onChange={handleInputChange} />
                      <Input label="Frequency of Work" name="frequency" type="select" value={formData.frequency} onChange={handleInputChange}>
                        <option value="one-time">One Time</option>
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                        <option value="bi-weekly">Bi-Weekly</option>
                        <option value="monthly">Monthly</option>
                        <option value="bi-monthly">Bi-Monthly</option>
                        <option value="quarterly">Quarterly</option>
                        <option value="semi-annually">Semi-Annually</option>
                        <option value="annually">Annually</option>
                        <option value="as-needed">As Needed</option>
                      </Input>
                    </div>
                    <div className="mt-4">
                      <label className="block text-sm font-bold text-gray-700 mb-1">Scope of Work</label>
                      <textarea name="scope_of_work" rows="3" className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-primary focus:border-primary p-3 border" placeholder="Describe the required services..." value={formData.scope_of_work} onChange={handleInputChange}></textarea>
                    </div>
                </Section>

                <Section title="Verification & Pricing" icon={<ShieldIcon />}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:bg-gray-50 transition">
                        <Upload className="mx-auto h-12 w-12 text-blue-400 mb-3" />
                        <label className="cursor-pointer block">
                          <span className="text-primary font-bold hover:underline">Click to Upload Proof</span>
                          <input type="file" className="hidden" onChange={handleFileChange} required />
                        </label>
                        <p className="text-xs text-gray-400 mt-2">Email screenshot or Call recording</p>
                        {formData.proof_file && <p className="text-green-600 text-sm mt-2 font-bold bg-green-50 py-1 px-2 rounded inline-block">{formData.proof_file.name}</p>}
                      </div>
                      <div className="space-y-4">
                        <Input label="Suggested Price ($)" name="price_company" type="number" placeholder="250" value={formData.price_company} onChange={handleInputChange} />
                        <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                            <p className="text-sm text-blue-800 font-medium"><span className="font-bold">Note:</span> Your payout amount will be determined and approved by the Admin.</p>
                        </div>
                      </div>
                    </div>
                </Section>

                <div className="pt-6 border-t border-gray-100">
                  <button type="submit" className="w-full bg-gradient-to-r from-primary to-blue-700 hover:from-blue-600 hover:to-blue-800 text-white font-bold py-4 rounded-xl shadow-lg transform active:scale-95 transition-all text-lg">
                    Submit Lead for Approval
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

// --- HELPER COMPONENTS ---

const LeadDetailsBlock = ({ lead, isMobile }) => (
  <div className={`grid ${isMobile ? 'grid-cols-1' : 'grid-cols-2'} gap-4 text-sm text-gray-700`}>
    <div className="space-y-2">
        <p><span className="font-bold text-gray-500 w-24 inline-block">Decision Maker:</span> {lead.decision_maker_name}</p>
        <p><span className="font-bold text-gray-500 w-24 inline-block">Contact:</span> {lead.phone} • {lead.email}</p>
        <p><span className="font-bold text-gray-500 w-24 inline-block">Address:</span> {lead.address}</p>
    </div>
    <div className="space-y-2">
        <p><span className="font-bold text-gray-500 w-24 inline-block">Facility Type:</span> {lead.facility_type || 'N/A'}</p>
        <p><span className="font-bold text-gray-500 w-24 inline-block">Company Size:</span> {lead.company_size || 'N/A'}</p>
        <p><span className="font-bold text-gray-500 w-24 inline-block">Frequency:</span> {lead.frequency}</p>
    </div>
    <div className={`${isMobile ? 'col-span-1' : 'col-span-2'} bg-white p-3 rounded border border-gray-200 mt-2`}>
        <span className="font-bold text-gray-500 block mb-1">Scope of Work:</span>
        {lead.scope_of_work}
    </div>
  </div>
);

const SortableHeader = ({ label, sortKey, activeSort, onSort }) => (
    <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase cursor-pointer hover:text-primary transition-colors group" onClick={() => onSort(sortKey)}>
      <div className="flex items-center">
        {label}
        <ArrowUpDown size={14} className={`ml-1 transition-opacity ${activeSort.key === sortKey ? 'opacity-100 text-primary' : 'opacity-30 group-hover:opacity-60'}`} />
      </div>
    </th>
);

const StatCard = ({ icon, label, value, delay }) => (
  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay }} className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex items-center hover:shadow-md transition-shadow">
    <div className="p-3 rounded-full bg-blue-50 mr-3 shadow-sm">{icon}</div>
    <div>
      <p className="text-[10px] md:text-xs font-bold text-gray-500 uppercase">{label}</p>
      <p className="text-xl md:text-2xl font-black text-gray-900">{value}</p>
    </div>
  </motion.div>
);

const Section = ({ title, icon, children }) => (
  <div className="bg-gray-50 p-6 rounded-xl border border-gray-100">
    <div className="flex items-center mb-4">
      <div className="text-primary mr-2">{icon}</div>
      <h4 className="text-lg font-bold text-gray-800">{title}</h4>
    </div>
    {children}
  </div>
);

const Input = ({ label, type = "text", children, ...props }) => (
  <div>
    <label className="block text-sm font-bold text-gray-700 mb-1">{label}</label>
    {type === 'select' ? (
      <select className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-primary focus:border-primary p-3 border bg-white" {...props}>{children}</select>
    ) : (
      <input type={type} className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-primary focus:border-primary p-3 border" {...props} />
    )}
  </div>
);

const StatusBadge = ({ status }) => {
  const styles = {
    APPROVED: 'bg-green-100 text-green-800 border-green-200',
    PURCHASED: 'bg-purple-100 text-purple-800 border-purple-200',
    PENDING: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    REJECTED: 'bg-red-100 text-red-800 border-red-200'
  };
  return <span className={`px-3 py-1 inline-flex text-xs leading-5 font-bold rounded-full border ${styles[status] || styles.PENDING}`}>{status}</span>;
};

// Icons
const BuildingIcon = () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>;
const PersonIcon = () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>;
const CalendarIcon = () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>;
const ShieldIcon = () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;

export default SetterDashboard;